/**
 * Perfil Completo da Clínica — redesign moderno
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, Card, CardContent, Button,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, CircularProgress, Alert, Avatar, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Tooltip, LinearProgress,
  Collapse, InputAdornment, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import ArrowBackIcon    from '@mui/icons-material/ArrowBack';
import BusinessIcon     from '@mui/icons-material/Business';
import TrendingUpIcon   from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GroupIcon        from '@mui/icons-material/Group';
import PersonIcon       from '@mui/icons-material/Person';
import EmailIcon        from '@mui/icons-material/Email';
import PhoneIcon        from '@mui/icons-material/Phone';
import LocationOnIcon   from '@mui/icons-material/LocationOn';
import BadgeIcon        from '@mui/icons-material/Badge';
import ReceiptLongIcon     from '@mui/icons-material/ReceiptLong';
import FolderOpenIcon      from '@mui/icons-material/FolderOpen';
import AssignmentIcon      from '@mui/icons-material/Assignment';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import CloudUploadIcon     from '@mui/icons-material/CloudUpload';
import DownloadIcon        from '@mui/icons-material/Download';
import DeleteOutlineIcon   from '@mui/icons-material/DeleteOutline';
import EditNoteIcon        from '@mui/icons-material/EditNote';
import SearchIcon          from '@mui/icons-material/Search';
import ExpandMoreIcon      from '@mui/icons-material/ExpandMore';
import ExpandLessIcon      from '@mui/icons-material/ExpandLess';
import FilterListIcon      from '@mui/icons-material/FilterList';
import ReceiptIcon         from '@mui/icons-material/Receipt';
import ContentCopyIcon     from '@mui/icons-material/ContentCopy';
import EditIcon            from '@mui/icons-material/Edit';
import AttachFileIcon      from '@mui/icons-material/AttachFile';
import SwapHorizIcon       from '@mui/icons-material/SwapHoriz';
import clinicaService      from '../../services/clinicaService';
import api                 from '../../services/api';

const MESES_PT = ['','Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

/* ─── palette ─── */
const ORANGE  = '#F97316';
const ORANGE_L = '#FB923C';
const BLUE    = '#2563EB';
const GREEN   = '#10B981';
const RED     = '#EF4444';
const AMBER   = '#F59E0B';
const PURPLE  = '#7C3AED';
const DARK    = '#0F172A';

const fmt = (v) =>
  `R$ ${parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Parse de data sem conversão de timezone (evita bug UTC→BRT virar dia anterior)
const parseDate = (d) => {
  if (!d) return null;
  const s = String(d).split('T')[0];
  const [y, m, day] = s.split('-').map(Number);
  return new Date(y, m - 1, day); // mês local, sem UTC
};

const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d).split('T')[0];
  const [y, m, day] = s.split('-');
  return `${day}/${m}/${y}`;
};

/* ─── KPI Card ─── */
const KpiCard = ({ title, value, icon: Icon, gradient, sub }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#fff',
      overflow: 'hidden',
      transition: 'transform 0.18s, box-shadow 0.18s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' },
    }}
  >
    <Box sx={{ height: 4, background: gradient }} />
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
            {value}
          </Typography>
          {sub && <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.4 }}>{sub}</Typography>}
        </Box>
        <Box
          sx={{
            width: 44, height: 44, borderRadius: 2,
            background: gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(0,0,0,0.15)', flexShrink: 0,
          }}
        >
          <Icon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

/* ─── Tab customizada ─── */
const TabItem = ({ label, active, onClick, count }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 2, py: 1.2,
      cursor: 'pointer',
      borderBottom: active ? `2px solid ${ORANGE}` : '2px solid transparent',
      transition: 'all 0.18s',
      display: 'flex', alignItems: 'center', gap: 0.8,
      '&:hover': { bgcolor: 'rgba(249,115,22,0.04)' },
    }}
  >
    <Typography
      sx={{
        fontSize: 13, fontWeight: active ? 700 : 500,
        color: active ? ORANGE : '#64748B',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    {count !== undefined && (
      <Box
        sx={{
          minWidth: 20, height: 18, px: 0.6,
          borderRadius: 10,
          bgcolor: active ? 'rgba(249,115,22,0.12)' : '#F1F5F9',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: active ? ORANGE : '#94A3B8' }}>
          {count}
        </Typography>
      </Box>
    )}
  </Box>
);

/* ─── Info Row ─── */
const InfoRow = ({ icon: Icon, label, value, color }) => (
  value ? (
    <Box
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 2,
        p: 1.5, borderRadius: 2,
        '&:hover': { bgcolor: '#F8FAFC' },
        transition: 'background 0.15s',
      }}
    >
      <Box
        sx={{
          width: 34, height: 34, borderRadius: 2, flexShrink: 0,
          bgcolor: 'rgba(249,115,22,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon sx={{ fontSize: 17, color: color || ORANGE }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: '#1E293B', fontWeight: 500, mt: 0.2 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  ) : null
);

/* ─── Table header cell ─── */
const TH = ({ children, align = 'left' }) => (
  <TableCell
    align={align}
    sx={{
      fontSize: 10, fontWeight: 700, color: '#94A3B8',
      letterSpacing: '0.07em', textTransform: 'uppercase',
      borderBottom: '2px solid #F1F5F9', pb: 1.2, bgcolor: '#fff',
    }}
  >
    {children}
  </TableCell>
);

const TD = ({ children, align = 'left', sx: sxProp }) => (
  <TableCell
    align={align}
    sx={{ fontSize: 13, color: '#475569', borderBottom: '1px solid #F8FAFC', py: 1.5, ...sxProp }}
  >
    {children}
  </TableCell>
);

/* ═══════════════════════════════════════
   Componente Principal
═══════════════════════════════════════ */
const PerfilClinica = () => {
  const { clinicaId } = useParams();
  const navigate       = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [tab,     setTab]     = useState(0);
  const [dados,   setDados]   = useState(null);

  const [togglingTipoIds, setTogglingTipoIds] = useState(new Set());

  /* ─── Carnê Leão / Recibos PF ─── */
  const [pfFaturamentos, setPfFaturamentos] = useState([]);
  const [carneLoading,   setCarneLoading]   = useState(false);
  const [uploadingIds,   setUploadingIds]   = useState(new Set());
  const [carneSearch,    setCarneSearch]    = useState('');
  const [carneAno,       setCarneAno]       = useState('todos');
  const [carneStatus,    setCarneStatus]    = useState('todos'); // 'todos' | 'com_recibo' | 'sem_recibo'
  const [mesesAbertos,   setMesesAbertos]   = useState(new Set());
  const [togglingIds,    setTogglingIds]    = useState(new Set());
  const [emitindoIds,    setEmitindoIds]    = useState(new Set());
  const [cancelandoIds,  setCancelandoIds]  = useState(new Set());
  const [removendoAnexoIds, setRemovendoAnexoIds] = useState(new Set());
  const [visualizandoIds, setVisualizandoIds] = useState(new Set());

  /* Modal de emissão manual de NF */
  const [modalManual, setModalManual]       = useState({ open: false, faturamento: null });
  const [manualNumero, setManualNumero]     = useState('');
  const [manualFile,   setManualFile]       = useState(null);
  const [manualSaving, setManualSaving]     = useState(false);
  const [manualMsg,    setManualMsg]        = useState({ tipo: '', texto: '' });
  const [copiado,      setCopiado]          = useState('');

  /* ─── Notas Fiscais PJ ─── */
  const [nfSearch,       setNfSearch]       = useState('');
  const [nfAno,          setNfAno]          = useState('todos');
  const [nfStatus,       setNfStatus]       = useState('todos');
  const [nfMesesAbertos, setNfMesesAbertos] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const r = await clinicaService.getPerfilCompletoClinica(clinicaId);
        setDados(r);
      } catch {
        setError('Erro ao carregar dados da clínica');
      } finally {
        setLoading(false);
      }
    })();
  }, [clinicaId]);

  const carregarCarneLeao = async () => {
    try {
      setCarneLoading(true);
      const r = await clinicaService.listarCarneLeao(clinicaId);
      const lista = r.faturamentos || [];
      setPfFaturamentos(lista);
      // Abre automaticamente o mês mais recente
      if (lista.length > 0) {
        const primeiroItem = lista[0];
        const d = parseDate(primeiroItem.data);
        const chave = `${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
        setMesesAbertos(new Set([chave]));
      }
    } catch (err) {
      console.error('Erro ao carregar lançamentos PF:', err);
    } finally {
      setCarneLoading(false);
    }
  };

  const toggleMes = (chave) => {
    setMesesAbertos(prev => {
      const n = new Set(prev);
      if (n.has(chave)) n.delete(chave); else n.add(chave);
      return n;
    });
  };

  const handleToggleCarneLeao = async (despesaId) => {
    setTogglingIds(prev => new Set([...prev, despesaId]));
    try {
      const r = await clinicaService.toggleCarneLeaoDespesa(despesaId);
      setDados(prev => ({
        ...prev,
        despesas: prev.despesas.map(d =>
          d.id === despesaId ? { ...d, usadoCarneLeao: r.usado_carne_leao } : d
        ),
      }));
    } catch (err) {
      console.error('Erro ao toggle carnê-leão:', err);
    } finally {
      setTogglingIds(prev => { const n = new Set(prev); n.delete(despesaId); return n; });
    }
  };

  /* Upload de recibo — disparado ao selecionar arquivo na linha */
  const handleUploadRecibo = async (faturamentoId, file) => {
    if (!file) return;
    setUploadingIds(prev => new Set([...prev, faturamentoId]));
    try {
      const form = new FormData();
      form.append('recibo', file);
      const r = await clinicaService.uploadRecibo(faturamentoId, form);
      setPfFaturamentos(prev => prev.map(f =>
        f.id === faturamentoId
          ? { ...f, reciboNome: r.reciboNome, reciboTamanho: r.reciboTamanho }
          : f
      ));
    } catch (err) {
      console.error('Erro ao fazer upload do recibo:', err);
      alert('Erro ao fazer upload. Tente novamente.');
    } finally {
      setUploadingIds(prev => { const n = new Set(prev); n.delete(faturamentoId); return n; });
    }
  };

  /* Download do recibo */
  const handleDownloadRecibo = async (f) => {
    try {
      await clinicaService.downloadRecibo(f.id, f.reciboNome);
    } catch (err) {
      console.error('Erro ao baixar recibo:', err);
      alert('Erro ao baixar o recibo.');
    }
  };

  /* Remover recibo */
  const handleRemoverRecibo = async (faturamentoId) => {
    if (!window.confirm('Remover o recibo anexado?')) return;
    try {
      await clinicaService.removerRecibo(faturamentoId);
      setPfFaturamentos(prev => prev.map(f =>
        f.id === faturamentoId ? { ...f, reciboNome: null, reciboTamanho: null } : f
      ));
    } catch (err) {
      console.error('Erro ao remover recibo:', err);
    }
  };

  /* Cancelar nota fiscal pelo admin */
  const handleCancelarNota = async (faturamentoId) => {
    const motivo = window.prompt('Informe o motivo do cancelamento:', 'Cancelamento solicitado pelo administrador.');
    if (motivo === null) return; // usuário clicou em Cancelar no prompt
    setCancelandoIds(prev => new Set([...prev, faturamentoId]));
    try {
      await clinicaService.cancelarNotaAdmin(faturamentoId, motivo);
      setDados(prev => ({
        ...prev,
        faturamentos: prev.faturamentos.map(f =>
          f.id === faturamentoId ? { ...f, notaEmitida: false, numeroNota: null } : f
        ),
      }));
    } catch (err) {
      const msg = err.response?.data?.message || 'Erro ao cancelar nota fiscal.';
      alert(msg);
    } finally {
      setCancelandoIds(prev => { const n = new Set(prev); n.delete(faturamentoId); return n; });
    }
  };

  /* Remove anexo de nota manual (ex: PDF errado subido por engano) — só pra notas #MANUAL */
  const handleRemoverAnexoManual = async (faturamentoId) => {
    if (!window.confirm('Remover o anexo dessa nota manual? O lançamento continua, só o PDF/número anexado é removido.')) return;
    setRemovendoAnexoIds(prev => new Set([...prev, faturamentoId]));
    try {
      await api.delete(`/operacional/faturamentos/${faturamentoId}/nota-manual`);
      setDados(prev => ({
        ...prev,
        faturamentos: prev.faturamentos.map(f =>
          f.id === faturamentoId ? { ...f, notaEmitida: false, numeroNota: null, notaFiscalUrl: null } : f
        ),
      }));
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao remover anexo da nota.');
    } finally {
      setRemovendoAnexoIds(prev => { const n = new Set(prev); n.delete(faturamentoId); return n; });
    }
  };

  /* Toggle PF ↔ PJ */
  const handleToggleTipo = async (fat) => {
    const novoTipo = (fat.tipo_pessoa || fat.tipoPessoa) === 'PF' ? 'PJ' : 'PF';
    setTogglingTipoIds(prev => new Set([...prev, fat.id]));
    try {
      await api.patch(`/operacional/faturamentos/${fat.id}/tipo`, { tipoPessoa: novoTipo });
      setDados(prev => ({
        ...prev,
        faturamentos: prev.faturamentos.map(f =>
          f.id === fat.id ? { ...f, tipo_pessoa: novoTipo, tipoPessoa: novoTipo } : f
        ),
      }));
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao alterar tipo');
    } finally {
      setTogglingTipoIds(prev => { const n = new Set(prev); n.delete(fat.id); return n; });
    }
  };

  /* Modal de emissão manual */
  const abrirModalManual = (fat) => {
    setModalManual({ open: true, faturamento: fat });
    setManualNumero('');
    setManualFile(null);
    setManualMsg({ tipo: '', texto: '' });
  };
  const fecharModalManual = () => {
    setModalManual({ open: false, faturamento: null });
    setManualFile(null);
    setManualMsg({ tipo: '', texto: '' });
  };
  const copiarCampo = (texto, campo) => {
    navigator.clipboard.writeText(texto || '');
    setCopiado(campo);
    setTimeout(() => setCopiado(''), 1800);
  };
  const handleSalvarNotaManual = async () => {
    const fat = modalManual.faturamento;
    if (!fat) return;
    setManualSaving(true);
    setManualMsg({ tipo: '', texto: '' });
    try {
      const formData = new FormData();
      if (manualNumero) formData.append('numeroNota', manualNumero);
      if (manualFile)   formData.append('nota', manualFile);
      const res = await api.post(`/operacional/faturamentos/${fat.id}/nota-manual`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const notaUrl = res?.notaUrl;
      setDados(prev => ({
        ...prev,
        faturamentos: prev.faturamentos.map(f =>
          f.id === fat.id ? { ...f, notaEmitida: true, numeroNota: manualNumero || `MANUAL`, notaFiscalUrl: notaUrl || f.notaFiscalUrl } : f
        ),
      }));
      setManualMsg({ tipo: 'success', texto: 'Nota registrada com sucesso!' });
      setTimeout(() => fecharModalManual(), 1500);
    } catch (err) {
      setManualMsg({ tipo: 'error', texto: err.response?.data?.message || 'Erro ao salvar.' });
    } finally {
      setManualSaving(false);
    }
  };

  /* Emitir nota fiscal pelo admin */
  const handleEmitirNota = async (faturamentoId) => {
    if (!window.confirm('Emitir nota fiscal para este lançamento?')) return;
    setEmitindoIds(prev => new Set([...prev, faturamentoId]));
    try {
      await clinicaService.emitirNotaAdmin(faturamentoId);
      setDados(prev => ({
        ...prev,
        faturamentos: prev.faturamentos.map(f =>
          f.id === faturamentoId ? { ...f, notaEmitida: true } : f
        ),
      }));
    } catch (err) {
      const msg = err.response?.data?.message || 'Erro ao emitir nota fiscal.';
      alert(msg);
    } finally {
      setEmitindoIds(prev => { const n = new Set(prev); n.delete(faturamentoId); return n; });
    }
  };

  /* Visualizar PDF da nota fiscal anexada manualmente */
  const handleVisualizarNota = async (faturamentoId) => {
    setVisualizandoIds(prev => new Set([...prev, faturamentoId]));
    try {
      await clinicaService.visualizarNotaManual(faturamentoId);
    } catch (err) {
      alert(err.message || 'Erro ao abrir a nota fiscal.');
    } finally {
      setVisualizandoIds(prev => { const n = new Set(prev); n.delete(faturamentoId); return n; });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: ORANGE }} size={44} thickness={3} />
          <Typography sx={{ mt: 2, color: '#94A3B8', fontSize: 13 }}>Carregando clínica...</Typography>
        </Box>
      </Box>
    );
  }

  if (error || !dados?.clinica) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Clínica não encontrada'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/operacional')}>
          Voltar
        </Button>
      </Box>
    );
  }

  const { clinica, faturamentos = [], despesas = [], documentos = [], estatisticas } = dados;
  const initials = clinica.nome?.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || 'C';
  const enderecoFull = clinica.endereco
    ? `${clinica.endereco}${clinica.numero ? ', ' + clinica.numero : ''} — ${clinica.bairro || ''}, ${clinica.cidade || ''}/${clinica.estado || ''}`
    : null;

  const isPF = clinica.tipoPessoa === 'PF' || clinica.tipoPessoa === 'HIBRIDO';

  const pjFaturamentos = faturamentos.filter(f => (f.tipo_pessoa || f.tipoPessoa) === 'PJ' && f.declarar !== false && f.declarar !== 0);
  const nfPendentes    = pjFaturamentos.filter(f => !f.notaEmitida).length;

  // TAB_NF = 5, TAB_CARNE = 6 (se isPF)
  const TAB_NF    = 5;
  const TAB_CARNE = 6;

  const tabs = [
    { label: 'Informações' },
    { label: 'Usuários',       count: estatisticas.numeroUsuarios     },
    { label: 'Faturamentos',   count: estatisticas.numeroFaturamentos },
    { label: 'Despesas',       count: estatisticas.numeroDespesas     },
    { label: 'Documentos',     count: estatisticas.numeroDocumentos   },
    { label: 'Notas Fiscais',  count: nfPendentes || undefined        },
    ...(isPF ? [{ label: 'Carnê Leão', count: pfFaturamentos.filter(f => !f.reciboNome).length || undefined }] : []),
    { label: 'Uso do Sistema', count: dados.usoSistema ? `${dados.usoSistema.percentualAdocao}%` : undefined },
  ];
  const TAB_USO = tabs.length - 1;

  const handleTabChange = (i) => {
    setTab(i);
    if (isPF && i === TAB_CARNE && pfFaturamentos.length === 0) {
      carregarCarneLeao();
    }
  };

  return (
    <>
    <Box sx={{ pb: 4 }}>

      {/* ── Voltar ── */}
      <Button
        startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
        onClick={() => navigate('/admin/operacional')}
        sx={{
          mb: 2, fontSize: 13, fontWeight: 600, color: ORANGE,
          textTransform: 'none',
          '&:hover': { bgcolor: 'rgba(249,115,22,0.06)' },
        }}
      >
        Voltar para Operacional
      </Button>

      {/* ── Hero ── */}
      <Box
        sx={{
          mb: 3, p: 3, borderRadius: 3,
          background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 60%, #0C2340 100%)',
          position: 'relative', overflow: 'hidden',
        }}
      >
        {/* decoração */}
        <Box sx={{ position: 'absolute', right: -20, top: -20, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(249,115,22,0.07)' }} />
        <Box sx={{ position: 'absolute', right: 120, bottom: -40, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(37,99,235,0.06)' }} />

        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
          {/* avatar */}
          <Avatar
            sx={{
              width: 72, height: 72,
              background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`,
              fontSize: 24, fontWeight: 800,
              boxShadow: '0 4px 16px rgba(249,115,22,0.4)',
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>

          {/* info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
              {clinica.nome}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'rgba(147,197,253,0.85)', mt: 0.3 }}>
              {clinica.tipoPessoa === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'}
              {(clinica.cnpj || clinica.cpf) && ` · ${clinica.cnpj || clinica.cpf}`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.2, flexWrap: 'wrap' }}>
              <Chip
                label={clinica.ativo !== false ? 'Ativa' : 'Inativa'}
                size="small"
                sx={{
                  bgcolor: clinica.ativo !== false ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
                  color:   clinica.ativo !== false ? '#34D399' : '#F87171',
                  fontWeight: 700, fontSize: 11, height: 22, border: 'none',
                }}
              />
              {clinica.plano && (
                <Chip
                  label={`Plano: ${clinica.plano}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(249,115,22,0.15)', color: ORANGE_L, fontWeight: 700, fontSize: 11, height: 22, border: 'none' }}
                />
              )}
            </Box>
          </Box>

          {/* quick stat */}
          <Box
            sx={{
              display: 'flex', gap: 1.5, flexWrap: 'wrap',
              alignSelf: 'flex-start',
            }}
          >
            {[
              { label: 'Usuários',   val: estatisticas.numeroUsuarios   },
              { label: 'Faturam.',   val: estatisticas.numeroFaturamentos },
              { label: 'Documentos', val: estatisticas.numeroDocumentos  },
            ].map((s) => (
              <Box
                key={s.label}
                sx={{
                  px: 1.8, py: 0.9,
                  bgcolor: 'rgba(255,255,255,0.09)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 2, textAlign: 'center',
                }}
              >
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{s.val}</Typography>
                <Typography sx={{ fontSize: 10, color: 'rgba(147,197,253,0.7)', mt: 0.3 }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── KPI Cards ── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard
            title="Total Faturamento"
            value={fmt(estatisticas.totalFaturamento)}
            icon={TrendingUpIcon}
            gradient={`linear-gradient(135deg, ${GREEN} 0%, #34D399 100%)`}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard
            title="Total Despesas"
            value={fmt(estatisticas.totalDespesas)}
            icon={TrendingDownIcon}
            gradient={`linear-gradient(135deg, ${RED} 0%, #F87171 100%)`}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard
            title="Saldo"
            value={fmt(estatisticas.saldo)}
            icon={AccountBalanceWalletIcon}
            gradient={
              estatisticas.saldo >= 0
                ? `linear-gradient(135deg, ${BLUE} 0%, #60A5FA 100%)`
                : `linear-gradient(135deg, ${RED} 0%, #F87171 100%)`
            }
            sub={`Impostos est.: ${fmt(estatisticas.impostosEstimados)}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard
            title="Usuários"
            value={estatisticas.numeroUsuarios}
            icon={GroupIcon}
            gradient={`linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`}
            sub={`${estatisticas.numeroFaturamentos} faturamentos`}
          />
        </Grid>
      </Grid>

      {/* ── Card com abas ── */}
      <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#fff', overflow: 'hidden' }}>

        {/* tabs */}
        <Box
          sx={{
            display: 'flex', borderBottom: '1px solid #F1F5F9',
            overflowX: 'auto',
            '&::-webkit-scrollbar': { height: 3 },
            px: 1,
          }}
        >
          {tabs.map((t, i) => (
            <TabItem
              key={i}
              label={t.label}
              active={tab === i}
              onClick={() => handleTabChange(i)}
              count={t.count}
            />
          ))}
        </Box>

        <Box sx={{ p: 3 }}>

          {/* ── Tab 0: Informações ── */}
          {tab === 0 && (
            <Grid container spacing={3}>
              {/* dados */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0F172A', mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <BusinessIcon sx={{ fontSize: 17, color: ORANGE }} /> Dados da Clínica
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <InfoRow icon={BusinessIcon}   label="Nome"            value={clinica.nome} />
                  <InfoRow icon={BadgeIcon}       label={clinica.tipoPessoa === 'PJ' ? 'CNPJ' : 'CPF'} value={clinica.cnpj || clinica.cpf} />
                  <InfoRow icon={EmailIcon}       label="Email"          value={clinica.email} />
                  <InfoRow icon={PhoneIcon}       label="Telefone"       value={clinica.telefone} />
                  <InfoRow icon={LocationOnIcon}  label="Endereço"       value={enderecoFull} />
                </Box>
              </Grid>

              {/* financeiro */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0F172A', mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <AccountBalanceWalletIcon sx={{ fontSize: 17, color: ORANGE }} /> Financeiro Resumido
                </Typography>
                <Box
                  sx={{
                    bgcolor: '#F8FAFC', borderRadius: 2,
                    border: '1px solid #E2E8F0', p: 2.5,
                    display: 'flex', flexDirection: 'column', gap: 1.2,
                  }}
                >
                  {[
                    { label: 'Total Faturamento', val: fmt(estatisticas.totalFaturamento), color: GREEN  },
                    { label: 'Total Despesas',    val: fmt(estatisticas.totalDespesas),    color: RED    },
                  ].map((r) => (
                    <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: 13, color: '#64748B' }}>{r.label}</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: r.color }}>{r.val}</Typography>
                    </Box>
                  ))}

                  <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1E293B' }}>Saldo</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: estatisticas.saldo >= 0 ? GREEN : RED }}>
                      {fmt(estatisticas.saldo)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>Impostos estimados (6,5%)</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: AMBER }}>{fmt(estatisticas.impostosEstimados)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>Lucro Líquido</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: estatisticas.lucroLiquido >= 0 ? GREEN : RED }}>
                      {fmt(estatisticas.lucroLiquido)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* ── Tab 1: Usuários ── */}
          {tab === 1 && (
            <Box>
              {!clinica.usuarios?.length ? (
                <EmptyState icon={PersonIcon} text="Nenhum usuário cadastrado nesta clínica" />
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {clinica.usuarios.map((u) => (
                    <Box
                      key={u.id}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 2,
                        p: 1.8, borderRadius: 2,
                        border: '1px solid #F1F5F9',
                        bgcolor: '#FAFAFA',
                        '&:hover': { borderColor: 'rgba(249,115,22,0.2)', bgcolor: '#FFF7ED' },
                        transition: 'all 0.15s',
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 38, height: 38,
                          background: `linear-gradient(135deg, ${BLUE} 0%, #60A5FA 100%)`,
                          fontSize: 13, fontWeight: 700,
                        }}
                      >
                        {u.nome?.charAt(0)?.toUpperCase() || 'U'}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#1E293B' }}>{u.nome}</Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }}>{u.email}{u.profissao ? ` · ${u.profissao}` : ''}</Typography>
                      </Box>
                      <Chip
                        label={u.ativo ? 'Ativo' : 'Inativo'}
                        size="small"
                        sx={{
                          bgcolor: u.ativo ? 'rgba(16,185,129,0.1)' : '#F1F5F9',
                          color: u.ativo ? GREEN : '#94A3B8',
                          fontWeight: 700, fontSize: 11,
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* ── Tab 2: Faturamentos ── */}
          {tab === 2 && (
            <Box>
              {!faturamentos.length ? (
                <EmptyState icon={ReceiptLongIcon} text="Nenhum faturamento registrado" />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TH>Data</TH>
                        <TH>Descrição</TH>
                        <TH>Paciente</TH>
                        <TH>Lançado por</TH>
                        <TH>Tipo</TH>
                        <TH align="right">Valor</TH>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {faturamentos.filter(f => f.declarar !== false && f.declarar !== 0).map((f) => {
                        const tipo = f.tipo_pessoa || f.tipoPessoa || 'PF';
                        const toggling = togglingTipoIds.has(f.id);
                        const bloqueado = f.notaEmitida || f.nota_emitida || f.reciboNome || f.recibo_nome;
                        return (
                        <TableRow key={f.id} sx={{ '&:hover': { bgcolor: 'rgba(16,185,129,0.03)' } }}>
                          <TD>{fmtDate(f.data)}</TD>
                          <TD sx={{ maxWidth: 200 }}>
                            <Typography sx={{ fontSize: 13, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {f.descricao || '—'}
                            </Typography>
                          </TD>
                          <TD>{f.paciente || '—'}</TD>
                          <TD>{f.usuario?.nome || '—'}</TD>
                          <TD>
                            <Box display="flex" alignItems="center" gap={0.5}>
                            <Tooltip title={
                              bloqueado ? 'Nota/recibo já emitido — tipo não pode ser alterado' :
                              toggling   ? 'Alterando...' :
                                           `Clique para mudar para ${tipo === 'PF' ? 'PJ' : 'PF'}`
                            }>
                              <span>
                                <Chip
                                  label={toggling ? '...' : tipo}
                                  size="small"
                                  onClick={() => !toggling && !bloqueado && handleToggleTipo(f)}
                                  sx={{
                                    fontWeight: 700, fontSize: 11,
                                    cursor: bloqueado ? 'not-allowed' : 'pointer',
                                    bgcolor: tipo === 'PF' ? 'rgba(37,99,235,0.1)' : 'rgba(124,58,237,0.1)',
                                    color: tipo === 'PF' ? BLUE : PURPLE,
                                    border: `1px solid ${tipo === 'PF' ? 'rgba(37,99,235,0.3)' : 'rgba(124,58,237,0.3)'}`,
                                    '&:hover': { bgcolor: bloqueado ? undefined : tipo === 'PF' ? 'rgba(37,99,235,0.2)' : 'rgba(124,58,237,0.2)' },
                                    transition: 'all 0.15s',
                                    opacity: bloqueado || toggling ? 0.45 : 1,
                                  }}
                                />
                              </span>
                            </Tooltip>
                            {(f.declarar === false || f.declarar === 0) && (
                              <Chip label="Controle" size="small" sx={{ fontWeight: 700, fontSize: 10, bgcolor: '#6B7280', color: '#fff' }} />
                            )}
                            </Box>
                          </TD>
                          <TD align="right">
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: GREEN }}>{fmt(f.valor)}</Typography>
                          </TD>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* ── Tab 3: Despesas ── */}
          {tab === 3 && (
            <Box>
              {!despesas.length ? (
                <EmptyState icon={ReceiptLongIcon} text="Nenhuma despesa registrada" />
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, bgcolor: 'rgba(124,58,237,0.04)', borderRadius: 2, border: '1px solid rgba(124,58,237,0.12)' }}>
                    <AssignmentIcon sx={{ fontSize: 16, color: PURPLE }} />
                    <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>
                      Clique no ícone <strong style={{ color: PURPLE }}>Carnê-Leão</strong> em cada linha para marcar despesas já deduzidas no Carnê-Leão.
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TH>Data</TH>
                          <TH>Descrição</TH>
                          <TH>Categoria</TH>
                          <TH>Lançado por</TH>
                          <TH align="right">Valor</TH>
                          <TH align="center">Carnê-Leão</TH>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {despesas.map((d) => {
                          const toggling = togglingIds.has(d.id);
                          const usado = d.usadoCarneLeao || d.usado_carne_leao;
                          return (
                            <TableRow key={d.id} sx={{ '&:hover': { bgcolor: 'rgba(239,68,68,0.03)' }, opacity: toggling ? 0.6 : 1 }}>
                              <TD>{fmtDate(d.data)}</TD>
                              <TD sx={{ maxWidth: 200 }}>
                                <Typography sx={{ fontSize: 13, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {d.descricao || '—'}
                                </Typography>
                              </TD>
                              <TD>
                                {d.categoria ? (
                                  <Chip label={d.categoria} size="small"
                                    sx={{ bgcolor: 'rgba(245,158,11,0.1)', color: AMBER, fontWeight: 600, fontSize: 11, height: 20 }} />
                                ) : '—'}
                              </TD>
                              <TD>{d.usuario?.nome || '—'}</TD>
                              <TD align="right">
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: RED }}>{fmt(d.valor)}</Typography>
                              </TD>
                              <TD align="center">
                                <Tooltip title={usado ? 'Clique para desmarcar' : 'Marcar como usada no Carnê-Leão'}>
                                  <Box
                                    onClick={() => !toggling && handleToggleCarneLeao(d.id)}
                                    sx={{
                                      display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                      px: 1.2, py: 0.4, borderRadius: 1.5, cursor: toggling ? 'wait' : 'pointer',
                                      border: `1px solid ${usado ? 'rgba(124,58,237,0.35)' : '#E2E8F0'}`,
                                      bgcolor: usado ? 'rgba(124,58,237,0.08)' : 'transparent',
                                      transition: 'all 0.18s',
                                      '&:hover': { bgcolor: usado ? 'rgba(124,58,237,0.14)' : '#F8FAFC', borderColor: PURPLE },
                                    }}
                                  >
                                    {toggling
                                      ? <CircularProgress size={12} sx={{ color: PURPLE }} />
                                      : <AssignmentIcon sx={{ fontSize: 13, color: usado ? PURPLE : '#CBD5E1' }} />
                                    }
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: usado ? PURPLE : '#CBD5E1', whiteSpace: 'nowrap' }}>
                                      {usado ? 'Deduzida' : 'Marcar'}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              </TD>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}

          {/* ── Tab 4: Documentos ── */}
          {tab === 4 && (
            <Box>
              {!documentos.length ? (
                <EmptyState icon={FolderOpenIcon} text="Nenhum documento cadastrado" />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TH>Arquivo</TH>
                        <TH>Tipo</TH>
                        <TH>Enviado por</TH>
                        <TH>Data</TH>
                        <TH align="right">Valor</TH>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documentos.map((doc) => (
                        <TableRow key={doc.id} sx={{ '&:hover': { bgcolor: 'rgba(37,99,235,0.03)' } }}>
                          <TD>
                            <Typography sx={{ fontSize: 13, color: '#1E293B', fontWeight: 500 }}>
                              {doc.nomeArquivo}
                            </Typography>
                            {doc.descricao && (
                              <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{doc.descricao}</Typography>
                            )}
                          </TD>
                          <TD>
                            <Chip
                              label={doc.tipo}
                              size="small"
                              sx={{ bgcolor: 'rgba(37,99,235,0.08)', color: BLUE, fontWeight: 600, fontSize: 11, height: 20 }}
                            />
                          </TD>
                          <TD>{doc.usuario?.nome || 'Admin'}</TD>
                          <TD>{fmtDate(doc.createdAt)}</TD>
                          <TD align="right">
                            <Typography sx={{ fontSize: 13, fontWeight: doc.valor ? 700 : 400, color: doc.valor ? BLUE : '#CBD5E1' }}>
                              {doc.valor ? fmt(doc.valor) : '—'}
                            </Typography>
                          </TD>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* ── Tab 5: Notas Fiscais ── */}
          {tab === TAB_NF && (
            <NotasFiscaisTab
              faturamentos={pjFaturamentos}
              emitindoIds={emitindoIds}
              cancelandoIds={cancelandoIds}
              removendoAnexoIds={removendoAnexoIds}
              visualizandoIds={visualizandoIds}
              nfSearch={nfSearch}
              setNfSearch={setNfSearch}
              nfAno={nfAno}
              setNfAno={setNfAno}
              nfStatus={nfStatus}
              setNfStatus={setNfStatus}
              nfMesesAbertos={nfMesesAbertos}
              setNfMesesAbertos={setNfMesesAbertos}
              onEmitir={handleEmitirNota}
              onCancelar={handleCancelarNota}
              onRemoverAnexo={handleRemoverAnexoManual}
              onEmitirManual={abrirModalManual}
              onVisualizar={handleVisualizarNota}
            />
          )}

          {/* ── Tab 6: Carnê Leão ── */}
          {tab === TAB_CARNE && isPF && (
            <CarneLeaoTab
              pfFaturamentos={pfFaturamentos}
              carneLoading={carneLoading}
              uploadingIds={uploadingIds}
              carneSearch={carneSearch}
              setCarneSearch={setCarneSearch}
              carneAno={carneAno}
              setCarneAno={setCarneAno}
              carneStatus={carneStatus}
              setCarneStatus={setCarneStatus}
              mesesAbertos={mesesAbertos}
              toggleMes={toggleMes}
              onAtualizar={carregarCarneLeao}
              onUpload={handleUploadRecibo}
              onDownload={handleDownloadRecibo}
              onRemover={handleRemoverRecibo}
            />
          )}

          {/* ── Tab: Uso do Sistema ── */}
          {tab === TAB_USO && (
            <UsoSistemaTab usoSistema={dados.usoSistema} />
          )}

        </Box>
      </Card>

    </Box>

    {/* ══════ MODAL EMISSÃO MANUAL DE NOTA FISCAL ══════ */}
    <Dialog open={modalManual.open} onClose={fecharModalManual} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon sx={{ color: '#F59E0B' }} />
            <Box>
              <Typography variant="h6" fontWeight={700}>Emissão Manual de NF</Typography>
              <Typography variant="caption" color="text.secondary">
                {modalManual.faturamento?.paciente}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={fecharModalManual}>✕</IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {modalManual.faturamento && (() => {
          const f = modalManual.faturamento;
          const cpf = f.cpf || f.pagadorCpf || f.pagador_cpf || '—';
          const campos = [
            { label: 'Paciente / Tomador', valor: f.paciente || f.pagadorNome || '—', campo: 'paciente' },
            { label: 'CPF / CNPJ',         valor: cpf,                                  campo: 'cpf' },
            { label: 'Descrição',           valor: f.descricao || '—',                  campo: 'descricao' },
            { label: 'Valor',               valor: fmt(f.valor),                         campo: 'valor' },
            { label: 'Data',                valor: fmtDate(f.data),                      campo: 'data' },
          ];
          return (
            <Box display="flex" flexDirection="column" gap={2}>
              {/* Dados para copiar */}
              <Box sx={{ bgcolor: 'background.default', borderRadius: 2, p: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  📋 Dados para emitir no portal da prefeitura
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                {campos.map(({ label, valor, campo }) => (
                  <Box key={campo} display="flex" justifyContent="space-between" alignItems="center"
                    sx={{ py: 0.8, borderBottom: '1px dashed', borderColor: 'divider' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={600}>{valor}</Typography>
                    </Box>
                    <Tooltip title={copiado === campo ? 'Copiado!' : 'Copiar'}>
                      <IconButton size="small" onClick={() => copiarCampo(valor, campo)}
                        sx={{ color: copiado === campo ? GREEN : 'text.secondary' }}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>

              {/* Número da nota */}
              <TextField
                label="Número da Nota Fiscal (opcional)"
                value={manualNumero}
                onChange={e => setManualNumero(e.target.value)}
                size="small"
                fullWidth
                placeholder="Ex: 000123"
              />

              {/* Upload do PDF */}
              <Box sx={{ border: '2px dashed', borderColor: manualFile ? GREEN : 'divider',
                borderRadius: 2, p: 2, textAlign: 'center', cursor: 'pointer',
                bgcolor: manualFile ? 'rgba(16,185,129,0.04)' : 'background.default',
                transition: 'all 0.2s' }}>
                <label style={{ cursor: 'pointer', display: 'block' }}>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" hidden
                    onChange={e => setManualFile(e.target.files[0] || null)} />
                  <AttachFileIcon sx={{ color: manualFile ? GREEN : 'text.secondary', mb: 0.5 }} />
                  <Typography variant="body2" color={manualFile ? 'success.main' : 'text.secondary'} fontWeight={600}>
                    {manualFile ? manualFile.name : 'Anexar PDF da Nota Fiscal'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {manualFile ? 'Clique para trocar o arquivo' : 'PDF, JPG ou PNG — até 10MB'}
                  </Typography>
                </label>
              </Box>

              {manualMsg.texto && (
                <Alert severity={manualMsg.tipo}>{manualMsg.texto}</Alert>
              )}
            </Box>
          );
        })()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" onClick={fecharModalManual} disabled={manualSaving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSalvarNotaManual}
          disabled={manualSaving}
          sx={{ bgcolor: '#F59E0B', '&:hover': { bgcolor: '#D97706' }, fontWeight: 700 }}
          startIcon={manualSaving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
        >
          {manualSaving ? 'Salvando...' : 'Marcar como Emitida'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

/* ══════════════════════════════════════════════════════
   Notas Fiscais PJ — aba exclusiva para admin emitir
══════════════════════════════════════════════════════ */
const NF_BLUE   = '#2563EB';
const NF_GREEN  = '#10B981';
const NF_AMBER  = '#F59E0B';

const NotasFiscaisTab = ({
  faturamentos, emitindoIds, cancelandoIds, removendoAnexoIds, visualizandoIds,
  nfSearch, setNfSearch,
  nfAno, setNfAno,
  nfStatus, setNfStatus,
  nfMesesAbertos, setNfMesesAbertos,
  onEmitir, onCancelar, onRemoverAnexo, onEmitirManual, onVisualizar,
}) => {
  const anos = useMemo(() => {
    const set = new Set(faturamentos.map(f => parseDate(f.data)?.getFullYear()));
    return ['todos', ...Array.from(set).sort((a, b) => b - a)];
  }, [faturamentos]);

  const filtrados = useMemo(() => {
    const q = nfSearch.toLowerCase().trim();
    return faturamentos.filter(f => {
      const anoOk    = nfAno === 'todos' || parseDate(f.data)?.getFullYear() === Number(nfAno);
      const statusOk = nfStatus === 'todos'
        || (nfStatus === 'emitida'  &&  f.notaEmitida)
        || (nfStatus === 'pendente' && !f.notaEmitida);
      const searchOk = !q
        || (f.paciente  || '').toLowerCase().includes(q)
        || (f.descricao || '').toLowerCase().includes(q)
        || (f.cpf       || '').replace(/\D/g,'').includes(q.replace(/\D/g,''));
      return anoOk && statusOk && searchOk;
    });
  }, [faturamentos, nfSearch, nfAno, nfStatus]);

  const porMes = useMemo(() => {
    const map = {};
    filtrados.forEach(f => {
      const d   = parseDate(f.data);
      const mes = d.getMonth() + 1;
      const ano = d.getFullYear();
      const chave = `${String(mes).padStart(2,'0')}/${ano}`;
      const label = `${MESES_PT[mes]}/${ano}`;
      if (!map[chave]) map[chave] = { chave, label, mes, ano, itens: [], total: 0, pendentes: 0 };
      map[chave].itens.push(f);
      map[chave].total += parseFloat(f.valor || 0);
      if (!f.notaEmitida) map[chave].pendentes++;
    });
    return Object.values(map).sort((a, b) => b.ano - a.ano || b.mes - a.mes);
  }, [filtrados]);

  const toggleMes = (chave) => {
    setNfMesesAbertos(prev => {
      const n = new Set(prev);
      if (n.has(chave)) n.delete(chave); else n.add(chave);
      return n;
    });
  };

  const totalGeral   = filtrados.reduce((s, f) => s + parseFloat(f.valor || 0), 0);
  const totalEmitida = faturamentos.filter(f => f.notaEmitida).reduce((s, f) => s + parseFloat(f.valor || 0), 0);
  const qtdEmitidas  = faturamentos.filter(f =>  f.notaEmitida).length;
  const qtdPendentes = faturamentos.filter(f => !f.notaEmitida).length;

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0F172A' }}>
            Notas Fiscais (PJ)
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8', mt: 0.2 }}>
            {filtrados.length} lançamentos · {fmt(totalGeral)} total
          </Typography>
        </Box>

        {/* KPIs rápidos */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', textAlign: 'center', minWidth: 90 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: NF_GREEN, lineHeight: 1 }}>{qtdEmitidas}</Typography>
            <Typography sx={{ fontSize: 10, color: NF_GREEN, fontWeight: 600, mt: 0.3 }}>Emitidas</Typography>
            <Typography sx={{ fontSize: 10, color: '#94A3B8', mt: 0.2 }}>{fmt(totalEmitida)}</Typography>
          </Box>
          <Box sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', textAlign: 'center', minWidth: 90 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: NF_AMBER, lineHeight: 1 }}>{qtdPendentes}</Typography>
            <Typography sx={{ fontSize: 10, color: NF_AMBER, fontWeight: 600, mt: 0.3 }}>Pendentes</Typography>
            <Typography sx={{ fontSize: 10, color: '#94A3B8', mt: 0.2 }}>{fmt(totalGeral - totalEmitida)}</Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Filtros ── */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap', alignItems: 'center',
        p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
        <FilterListIcon sx={{ fontSize: 18, color: '#94A3B8', flexShrink: 0 }} />

        <TextField
          size="small" placeholder="Buscar por paciente, CPF ou descrição..."
          value={nfSearch} onChange={e => setNfSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2, fontSize: 13 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#CBD5E1' }} /></InputAdornment> }}
        />

        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel sx={{ fontSize: 13 }}>Ano</InputLabel>
          <Select value={nfAno} onChange={e => setNfAno(e.target.value)} label="Ano"
            sx={{ bgcolor: '#fff', borderRadius: 2, fontSize: 13 }}>
            {anos.map(a => <MenuItem key={a} value={a} sx={{ fontSize: 13 }}>{a === 'todos' ? 'Todos os anos' : a}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel sx={{ fontSize: 13 }}>Status</InputLabel>
          <Select value={nfStatus} onChange={e => setNfStatus(e.target.value)} label="Status"
            sx={{ bgcolor: '#fff', borderRadius: 2, fontSize: 13 }}>
            <MenuItem value="todos"    sx={{ fontSize: 13 }}>Todos</MenuItem>
            <MenuItem value="emitida"  sx={{ fontSize: 13 }}>Emitidas</MenuItem>
            <MenuItem value="pendente" sx={{ fontSize: 13 }}>Pendentes</MenuItem>
          </Select>
        </FormControl>

        {(nfSearch || nfAno !== 'todos' || nfStatus !== 'todos') && (
          <Button size="small" onClick={() => { setNfSearch(''); setNfAno('todos'); setNfStatus('todos'); }}
            sx={{ textTransform: 'none', fontSize: 12, color: '#94A3B8', '&:hover': { color: '#EF4444' } }}>
            Limpar filtros
          </Button>
        )}
      </Box>

      {!faturamentos.length && (
        <EmptyState icon={ReceiptIcon} text="Nenhum lançamento PJ encontrado para esta clínica" />
      )}
      {faturamentos.length > 0 && filtrados.length === 0 && (
        <EmptyState icon={SearchIcon} text="Nenhum lançamento encontrado com os filtros aplicados" />
      )}

      {/* ── Acordeão por mês ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {porMes.map(({ chave, label, itens, total, pendentes }) => {
          const aberto = nfMesesAbertos.has(chave);
          return (
            <Box key={chave} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden' }}>

              {/* ── Cabeçalho do mês ── */}
              <Box
                onClick={() => toggleMes(chave)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  px: 2.5, py: 1.5, cursor: 'pointer',
                  bgcolor: aberto ? 'rgba(37,99,235,0.04)' : '#FAFAFA',
                  borderBottom: aberto ? '1px solid #E2E8F0' : 'none',
                  transition: 'background 0.15s',
                  '&:hover': { bgcolor: 'rgba(37,99,235,0.06)' },
                }}
              >
                {aberto
                  ? <ExpandLessIcon sx={{ fontSize: 20, color: NF_BLUE }} />
                  : <ExpandMoreIcon sx={{ fontSize: 20, color: '#94A3B8' }} />}

                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1E293B', flex: 1 }}>
                  {label}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip label={`${itens.length} lançamento${itens.length !== 1 ? 's' : ''}`}
                    size="small" sx={{ bgcolor: 'rgba(37,99,235,0.08)', color: NF_BLUE, fontWeight: 700, fontSize: 11, height: 22 }} />
                  {pendentes > 0 && (
                    <Chip label={`${pendentes} pendente${pendentes !== 1 ? 's' : ''}`}
                      size="small" sx={{ bgcolor: 'rgba(245,158,11,0.1)', color: NF_AMBER, fontWeight: 700, fontSize: 11, height: 22 }} />
                  )}
                  {pendentes === 0 && (
                    <Chip label="Todas emitidas" icon={<CheckCircleIcon style={{ fontSize: 11 }} />}
                      size="small" sx={{ bgcolor: 'rgba(16,185,129,0.1)', color: NF_GREEN, fontWeight: 700, fontSize: 11, height: 22 }} />
                  )}
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: NF_GREEN, minWidth: 90, textAlign: 'right' }}>
                    {fmt(total)}
                  </Typography>
                </Box>
              </Box>

              {/* ── Tabela do mês ── */}
              <Collapse in={aberto}>
                <TableContainer>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #F8FAFC' } }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                        <TH>Data</TH>
                        <TH>Paciente / Empresa</TH>
                        <TH>Descrição</TH>
                        <TH>Dentista</TH>
                        <TH align="right">Valor</TH>
                        <TH align="center">Nota Fiscal</TH>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itens.map(f => {
                        const emitindo     = emitindoIds.has(f.id);
                        const cancelando   = cancelandoIds.has(f.id);
                        const removendoAnexo = removendoAnexoIds.has(f.id);
                        const visualizando = visualizandoIds.has(f.id);
                        const isNotaManual = !!f.numeroNota && String(f.numeroNota).startsWith('MANUAL');
                        return (
                          <TableRow key={f.id} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                            <TD sx={{ whiteSpace: 'nowrap' }}>{fmtDate(f.data)}</TD>
                            <TD>
                              <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1E293B' }}>
                                {f.paciente || '—'}
                              </Typography>
                              {f.cnpj && (
                                <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{f.cnpj}</Typography>
                              )}
                              {f.pagadorNome && f.pagadorNome !== f.paciente && (
                                <Tooltip title={`Nota deve ser emitida pra: ${f.pagadorNome}${f.pagadorCpf ? ' · ' + f.pagadorCpf : ''}`} arrow>
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, mt: 0.3,
                                    bgcolor: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)',
                                    borderRadius: 1, px: 0.8, py: 0.2 }}>
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#EA580C', whiteSpace: 'nowrap' }}>
                                      Pagador diferente: {f.pagadorNome}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              )}
                            </TD>
                            <TD sx={{ fontSize: 12, color: '#64748B', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {f.descricao || '—'}
                            </TD>
                            <TD sx={{ fontSize: 12, color: '#64748B' }}>
                              {f.usuario?.nome || '—'}
                            </TD>
                            <TD align="right">
                              <Typography sx={{ fontWeight: 700, color: NF_GREEN, fontSize: 13 }}>{fmt(f.valor)}</Typography>
                            </TD>
                            <TD align="center">
                              {f.notaEmitida ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                    bgcolor: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
                                    borderRadius: 1.5, px: 1.2, py: 0.5 }}>
                                    <CheckCircleIcon sx={{ fontSize: 13, color: NF_GREEN }} />
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: NF_GREEN, whiteSpace: 'nowrap' }}>
                                      Emitida
                                    </Typography>
                                    {f.numeroNota && (
                                      <Typography sx={{ fontSize: 10, color: '#94A3B8', ml: 0.5 }}>
                                        #{f.numeroNota}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.6 }}>
                                    {f.notaEmitida && (
                                      <Tooltip title="Visualizar PDF da nota fiscal">
                                        <Box
                                          onClick={() => !visualizando && onVisualizar(f.id)}
                                          sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                            px: 1, py: 0.3, borderRadius: 1.5,
                                            cursor: visualizando ? 'wait' : 'pointer',
                                            border: `1px solid rgba(37,99,235,0.3)`,
                                            bgcolor: 'rgba(37,99,235,0.05)',
                                            transition: 'all 0.18s',
                                            '&:hover': { bgcolor: 'rgba(37,99,235,0.12)', borderColor: NF_BLUE },
                                          }}
                                        >
                                          {visualizando
                                            ? <CircularProgress size={10} sx={{ color: NF_BLUE }} />
                                            : <DownloadIcon sx={{ fontSize: 12, color: NF_BLUE }} />
                                          }
                                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: NF_BLUE, whiteSpace: 'nowrap' }}>
                                            {visualizando ? 'Abrindo...' : 'Ver PDF'}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    )}
                                    {isNotaManual ? (
                                      <Tooltip title="Remover o anexo/número dessa nota manual (ex: PDF errado) — o lançamento continua, só desanexa">
                                        <Box
                                          onClick={() => !removendoAnexo && onRemoverAnexo(f.id)}
                                          sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                            px: 1, py: 0.3, borderRadius: 1.5,
                                            cursor: removendoAnexo ? 'wait' : 'pointer',
                                            border: '1px solid rgba(239,68,68,0.3)',
                                            bgcolor: 'rgba(239,68,68,0.05)',
                                            transition: 'all 0.18s',
                                            '&:hover': { bgcolor: 'rgba(239,68,68,0.12)', borderColor: '#EF4444' },
                                          }}
                                        >
                                          {removendoAnexo
                                            ? <CircularProgress size={10} sx={{ color: '#EF4444' }} />
                                            : <DeleteOutlineIcon sx={{ fontSize: 12, color: '#EF4444' }} />
                                          }
                                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#EF4444', whiteSpace: 'nowrap' }}>
                                            {removendoAnexo ? 'Removendo...' : 'Remover anexo'}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Cancelar nota fiscal na prefeitura">
                                        <Box
                                          onClick={() => !cancelando && onCancelar(f.id)}
                                          sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                            px: 1, py: 0.3, borderRadius: 1.5,
                                            cursor: cancelando ? 'wait' : 'pointer',
                                            border: '1px solid rgba(239,68,68,0.3)',
                                            bgcolor: 'rgba(239,68,68,0.05)',
                                            transition: 'all 0.18s',
                                            '&:hover': { bgcolor: 'rgba(239,68,68,0.12)', borderColor: '#EF4444' },
                                          }}
                                        >
                                          {cancelando
                                            ? <CircularProgress size={10} sx={{ color: '#EF4444' }} />
                                            : <DeleteOutlineIcon sx={{ fontSize: 12, color: '#EF4444' }} />
                                          }
                                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#EF4444', whiteSpace: 'nowrap' }}>
                                            {cancelando ? 'Cancelando...' : 'Cancelar NF'}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
                                  <Tooltip title="Emitir nota fiscal via API automaticamente">
                                    <Box
                                      onClick={() => !emitindo && onEmitir(f.id)}
                                      sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                        px: 1.4, py: 0.6, borderRadius: 1.5,
                                        cursor: emitindo ? 'wait' : 'pointer',
                                        border: `1px solid rgba(37,99,235,0.4)`,
                                        bgcolor: 'rgba(37,99,235,0.07)',
                                        transition: 'all 0.18s',
                                        '&:hover': { bgcolor: 'rgba(37,99,235,0.14)', borderColor: NF_BLUE, boxShadow: '0 2px 8px rgba(37,99,235,0.15)' },
                                      }}
                                    >
                                      {emitindo
                                        ? <CircularProgress size={12} sx={{ color: NF_BLUE }} />
                                        : <ReceiptIcon sx={{ fontSize: 13, color: NF_BLUE }} />
                                      }
                                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: NF_BLUE, whiteSpace: 'nowrap' }}>
                                        {emitindo ? 'Emitindo...' : 'Emitir NF'}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                  <Tooltip title="Emitir manualmente no portal da prefeitura e anexar PDF">
                                    <Box
                                      onClick={() => onEmitirManual(f)}
                                      sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                        px: 1.2, py: 0.6, borderRadius: 1.5,
                                        cursor: 'pointer',
                                        border: `1px solid rgba(245,158,11,0.4)`,
                                        bgcolor: 'rgba(245,158,11,0.07)',
                                        transition: 'all 0.18s',
                                        '&:hover': { bgcolor: 'rgba(245,158,11,0.15)', borderColor: '#F59E0B' },
                                      }}
                                    >
                                      <EditIcon sx={{ fontSize: 13, color: '#F59E0B' }} />
                                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#F59E0B', whiteSpace: 'nowrap' }}>
                                        Manual
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                </Box>
                              )}
                            </TD>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ══════════════════════════════════════════════════════
   Carnê Leão — separado por mês com filtros
══════════════════════════════════════════════════════ */
const CarneLeaoTab = ({
  pfFaturamentos, carneLoading, uploadingIds,
  carneSearch, setCarneSearch,
  carneAno, setCarneAno,
  carneStatus, setCarneStatus,
  mesesAbertos, toggleMes,
  onAtualizar, onUpload, onDownload, onRemover,
}) => {
  const PURPLE = '#7C3AED';

  // Anos disponíveis para filtro
  const anos = useMemo(() => {
    const set = new Set(pfFaturamentos.map(f => parseDate(f.data)?.getFullYear()));
    return ['todos', ...Array.from(set).sort((a, b) => b - a)];
  }, [pfFaturamentos]);

  // Filtra os lançamentos
  const filtrados = useMemo(() => {
    const q = carneSearch.toLowerCase().trim();
    return pfFaturamentos.filter(f => {
      const anoOk  = carneAno === 'todos' || parseDate(f.data)?.getFullYear() === Number(carneAno);
      const statOk = carneStatus === 'todos'
        || (carneStatus === 'com_recibo' && f.reciboNome)
        || (carneStatus === 'sem_recibo' && !f.reciboNome);
      const searchOk = !q
        || (f.paciente || '').toLowerCase().includes(q)
        || (f.cpf || '').replace(/\D/g,'').includes(q.replace(/\D/g,''))
        || (f.descricao || '').toLowerCase().includes(q);
      return anoOk && statOk && searchOk;
    });
  }, [pfFaturamentos, carneSearch, carneAno, carneStatus]);

  // Agrupa por mês
  const porMes = useMemo(() => {
    const map = {};
    filtrados.forEach(f => {
      const d = parseDate(f.data);
      const mes = d.getMonth() + 1;
      const ano = d.getFullYear();
      const chave = `${String(mes).padStart(2,'0')}/${ano}`;
      const label = `${MESES_PT[mes]}/${ano}`;
      if (!map[chave]) map[chave] = { chave, label, mes, ano, itens: [], total: 0, semRecibo: 0 };
      map[chave].itens.push(f);
      map[chave].total += parseFloat(f.valor || 0);
      if (!f.reciboNome) map[chave].semRecibo++;
    });
    return Object.values(map).sort((a, b) => b.ano - a.ano || b.mes - a.mes);
  }, [filtrados]);

  const totalGeral = filtrados.reduce((s, f) => s + parseFloat(f.valor || 0), 0);

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0F172A' }}>
            Carnê Leão / Receita Saúde
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8', mt: 0.2 }}>
            {filtrados.length} lançamentos · {fmt(totalGeral)} total
          </Typography>
        </Box>
        <Button size="small" startIcon={<AssignmentIcon />} onClick={onAtualizar} variant="outlined"
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', color: PURPLE, borderColor: `${PURPLE}40`, '&:hover': { bgcolor: `${PURPLE}08`, borderColor: PURPLE } }}>
          Atualizar
        </Button>
      </Box>

      {/* ── Filtros ── */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap', alignItems: 'center',
        p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
        <FilterListIcon sx={{ fontSize: 18, color: '#94A3B8', flexShrink: 0 }} />

        <TextField
          size="small" placeholder="Buscar por paciente, CPF ou descrição..."
          value={carneSearch} onChange={e => setCarneSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2, fontSize: 13 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#CBD5E1' }} /></InputAdornment> }}
        />

        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel sx={{ fontSize: 13 }}>Ano</InputLabel>
          <Select value={carneAno} onChange={e => setCarneAno(e.target.value)} label="Ano"
            sx={{ bgcolor: '#fff', borderRadius: 2, fontSize: 13 }}>
            {anos.map(a => <MenuItem key={a} value={a} sx={{ fontSize: 13 }}>{a === 'todos' ? 'Todos os anos' : a}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel sx={{ fontSize: 13 }}>Recibo</InputLabel>
          <Select value={carneStatus} onChange={e => setCarneStatus(e.target.value)} label="Recibo"
            sx={{ bgcolor: '#fff', borderRadius: 2, fontSize: 13 }}>
            <MenuItem value="todos"      sx={{ fontSize: 13 }}>Todos</MenuItem>
            <MenuItem value="com_recibo" sx={{ fontSize: 13 }}>Com recibo</MenuItem>
            <MenuItem value="sem_recibo" sx={{ fontSize: 13 }}>Sem recibo</MenuItem>
          </Select>
        </FormControl>

        {(carneSearch || carneAno !== 'todos' || carneStatus !== 'todos') && (
          <Button size="small" onClick={() => { setCarneSearch(''); setCarneAno('todos'); setCarneStatus('todos'); }}
            sx={{ textTransform: 'none', fontSize: 12, color: '#94A3B8', '&:hover': { color: '#EF4444' } }}>
            Limpar filtros
          </Button>
        )}
      </Box>

      {carneLoading && <LinearProgress sx={{ mb: 2, bgcolor: `${PURPLE}18`, '& .MuiLinearProgress-bar': { bgcolor: PURPLE } }} />}

      {!carneLoading && pfFaturamentos.length === 0 && (
        <EmptyState icon={AssignmentIcon} text="Nenhum lançamento PF encontrado para este dentista" />
      )}

      {!carneLoading && pfFaturamentos.length > 0 && filtrados.length === 0 && (
        <EmptyState icon={SearchIcon} text="Nenhum lançamento encontrado com os filtros aplicados" />
      )}

      {/* ── Acordeão por mês ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {porMes.map(({ chave, label, itens, total, semRecibo }) => {
          const aberto = mesesAbertos.has(chave);
          return (
            <Box key={chave} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden' }}>

              {/* ── Cabeçalho do mês ── */}
              <Box
                onClick={() => toggleMes(chave)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  px: 2.5, py: 1.5, cursor: 'pointer',
                  bgcolor: aberto ? 'rgba(124,58,237,0.04)' : '#FAFAFA',
                  borderBottom: aberto ? '1px solid #E2E8F0' : 'none',
                  transition: 'background 0.15s',
                  '&:hover': { bgcolor: 'rgba(124,58,237,0.06)' },
                }}
              >
                {aberto
                  ? <ExpandLessIcon sx={{ fontSize: 20, color: PURPLE }} />
                  : <ExpandMoreIcon sx={{ fontSize: 20, color: '#94A3B8' }} />}

                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1E293B', flex: 1 }}>
                  {label}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip label={`${itens.length} lançamento${itens.length !== 1 ? 's' : ''}`}
                    size="small" sx={{ bgcolor: 'rgba(124,58,237,0.08)', color: PURPLE, fontWeight: 700, fontSize: 11, height: 22 }} />
                  {semRecibo > 0 && (
                    <Chip label={`${semRecibo} sem recibo`}
                      size="small" sx={{ bgcolor: 'rgba(239,68,68,0.08)', color: '#EF4444', fontWeight: 700, fontSize: 11, height: 22 }} />
                  )}
                  {semRecibo === 0 && (
                    <Chip label="Todos com recibo" icon={<CheckCircleIcon style={{ fontSize: 11 }} />}
                      size="small" sx={{ bgcolor: 'rgba(16,185,129,0.1)', color: '#10B981', fontWeight: 700, fontSize: 11, height: 22 }} />
                  )}
                  <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#10B981', minWidth: 90, textAlign: 'right' }}>
                    {fmt(total)}
                  </Typography>
                </Box>
              </Box>

              {/* ── Tabela do mês ── */}
              <Collapse in={aberto}>
                <TableContainer>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #F8FAFC' } }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                        <TH>Data</TH>
                        <TH>Paciente</TH>
                        <TH>CPF</TH>
                        <TH>Descrição</TH>
                        <TH align="right">Valor</TH>
                        <TH align="center">Recibo</TH>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itens.map(f => {
                        const uploading = uploadingIds.has(f.id);
                        return (
                          <TableRow key={f.id} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                            <TD sx={{ whiteSpace: 'nowrap' }}>{fmtDate(f.data)}</TD>
                            <TD>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1E293B' }}>
                                  {f.paciente || '—'}
                                </Typography>
                                {f.pagadorNome && (
                                  <Tooltip title={`Pagador: ${f.pagadorNome}${f.pagadorCpf ? ' · ' + f.pagadorCpf : ''}`} arrow>
                                    <Chip
                                      icon={<SwapHorizIcon sx={{ fontSize: '12px !important' }} />}
                                      label="Pagador diferente"
                                      size="small"
                                      sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(124,58,237,0.08)', color: PURPLE, '& .MuiChip-icon': { color: PURPLE } }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TD>
                            <TD sx={{ fontSize: 12, color: '#64748B', whiteSpace: 'nowrap' }}>
                              {f.cpf || <span style={{ color: '#CBD5E1' }}>—</span>}
                            </TD>
                            <TD sx={{ fontSize: 12, color: '#64748B', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {f.descricao || '—'}
                            </TD>
                            <TD align="right">
                              <Typography sx={{ fontWeight: 700, color: '#10B981', fontSize: 13 }}>{fmt(f.valor)}</Typography>
                            </TD>
                            <TD align="center">
                              {uploading ? (
                                <CircularProgress size={18} sx={{ color: PURPLE }} />
                              ) : f.reciboNome ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                  <Tooltip title={f.reciboNome}>
                                    <Box onClick={() => onDownload(f)}
                                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, bgcolor: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
                                        borderRadius: 1.5, px: 1, py: 0.4, cursor: 'pointer', maxWidth: 150, transition: 'all 0.15s', '&:hover': { bgcolor: 'rgba(16,185,129,0.2)' } }}>
                                      <CheckCircleIcon sx={{ fontSize: 12, color: '#10B981', flexShrink: 0 }} />
                                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#10B981', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {f.reciboNome}
                                      </Typography>
                                      <DownloadIcon sx={{ fontSize: 11, color: '#10B981', flexShrink: 0, opacity: 0.7 }} />
                                    </Box>
                                  </Tooltip>
                                  <Box sx={{ display: 'flex', gap: 0.2 }}>
                                    <Tooltip title="Substituir">
                                      <IconButton size="small" component="label" sx={{ p: 0.3, color: PURPLE, '&:hover': { bgcolor: `rgba(124,58,237,0.1)` } }}>
                                        <EditNoteIcon sx={{ fontSize: 13 }} />
                                        <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={e => onUpload(f.id, e.target.files[0])} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remover">
                                      <IconButton size="small" onClick={() => onRemover(f.id)} sx={{ p: 0.3, color: '#EF4444', '&:hover': { bgcolor: 'rgba(239,68,68,0.1)' } }}>
                                        <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              ) : (
                                <Button size="small" variant="contained" component="label"
                                  startIcon={<CloudUploadIcon sx={{ fontSize: '12px !important' }} />}
                                  sx={{ bgcolor: PURPLE, fontSize: 11, fontWeight: 700, textTransform: 'none', borderRadius: 1.5, px: 1.2, py: 0.3, boxShadow: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: '#6D28D9' } }}>
                                  Anexar
                                  <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={e => onUpload(f.id, e.target.files[0])} />
                                </Button>
                              )}
                            </TD>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ─── Aba: Uso do Sistema ─── */
const CATEGORIAS_ORDEM = ['Pacientes', 'Agenda', 'Financeiro', 'Documentos & Assinaturas', 'Integrações'];

const UsoSistemaTab = ({ usoSistema }) => {
  if (!usoSistema) return <EmptyState icon={AssignmentIcon} text="Sem dados de uso ainda" />;

  const { itens, funcionalidadesUsadas, funcionalidadesTotal, percentualAdocao, maisUsada, menosUsada } = usoSistema;

  const corAdocao = percentualAdocao >= 60 ? GREEN : percentualAdocao >= 30 ? AMBER : RED;

  return (
    <Box>
      {/* resumo geral */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 3, p: 2.5, height: '100%' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
              Adoção do sistema
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1 }}>
              <Typography sx={{ fontSize: 34, fontWeight: 800, color: corAdocao, lineHeight: 1 }}>{percentualAdocao}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percentualAdocao}
              sx={{
                height: 8, borderRadius: 4, mb: 1,
                bgcolor: '#E2E8F0',
                '& .MuiLinearProgress-bar': { bgcolor: corAdocao, borderRadius: 4 },
              }}
            />
            <Typography sx={{ fontSize: 12, color: '#64748B' }}>
              {funcionalidadesUsadas} de {funcionalidadesTotal} funcionalidades usadas ao menos uma vez
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ bgcolor: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 3, p: 2.5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
              <TrendingUpIcon sx={{ fontSize: 16, color: GREEN }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: GREEN, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Mais usa
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0F172A', mb: 0.3 }}>{maisUsada?.label}</Typography>
            <Typography sx={{ fontSize: 12, color: '#64748B' }}>{maisUsada?.valor} registros</Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ bgcolor: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.18)', borderRadius: 3, p: 2.5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
              <TrendingDownIcon sx={{ fontSize: 16, color: RED }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: RED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Menos usa
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0F172A', mb: 0.3 }}>{menosUsada?.label}</Typography>
            <Typography sx={{ fontSize: 12, color: '#64748B' }}>{menosUsada?.valor} registros</Typography>
          </Box>
        </Grid>
      </Grid>

      {/* ranking por categoria */}
      {CATEGORIAS_ORDEM.filter((cat) => itens.some((i) => i.categoria === cat)).map((cat) => (
        <Box key={cat} sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#0F172A', mb: 1.2 }}>{cat}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
            {itens.filter((i) => i.categoria === cat).map((i) => (
              <Box key={i.chave} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ fontSize: 12.5, color: '#475569', width: 260, flexShrink: 0 }}>
                  {i.label}
                </Typography>
                <Box sx={{ flex: 1, position: 'relative', height: 16, bgcolor: '#F1F5F9', borderRadius: 1 }}>
                  <Box
                    sx={{
                      position: 'absolute', left: 0, top: 0, bottom: 0,
                      width: `${Math.max(i.percentualRelativo, i.valor > 0 ? 3 : 0)}%`,
                      bgcolor: i.valor > 0 ? ORANGE : 'transparent',
                      borderRadius: 1,
                      transition: 'width 0.3s',
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: i.valor > 0 ? '#1E293B' : '#CBD5E1', width: 40, textAlign: 'right', flexShrink: 0 }}>
                  {i.valor}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

/* ── Empty state reutilizável ── */
const EmptyState = ({ icon: Icon, text }) => (
  <Box sx={{ textAlign: 'center', py: 7 }}>
    <Icon sx={{ fontSize: 48, color: '#E2E8F0', mb: 1.5 }} />
    <Typography sx={{ fontSize: 14, color: '#CBD5E1', fontWeight: 600 }}>{text}</Typography>
  </Box>
);

export default PerfilClinica;
